/**
 * Feasibility report — the section at the top of Budget & Draws.
 *
 * On a GUC / Fix-and-Flip / RTL loan the lender is financing work that hasn't
 * happened yet; the third-party feasibility is the independent check on the
 * borrower's own budget. It decides the holdback (`constructionBudget ×
 * financedBudgetPercent`), it reveals work already done or paid for before
 * closing (the loan is then financing LESS than 100% of the budget), and when
 * it's required and missing that is a CRITICAL, not-waivable finding.
 *
 * The section answers, in order: is one required, was one ordered, is one on
 * file, has it been extracted, has it been reviewed — plus what the report
 * actually says and which properties drive the requirement.
 *
 * Layout:
 *   header    title · status pill · collapse
 *   strip     where it stands in one line + the Manage popover (always shown)
 *   rail      the five-step progression, with the blocking step marked
 *   body      LEFT  the cross-checks (what will run, or what they found)
 *                   + derived findings
 *             RIGHT the report itself (vendor / basis / date), or its empty state
 *   table     per property: criteria met · order status (editable)
 *
 * Data: three redux selectors, the extracts index, and
 * `GET /{prefix}/report_photo_reviews/{dealId}`. All derivation lives in
 * feasibilityModel.js — see its header for why the rollup is recomputed here
 * rather than fetched.
 */
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import UploadOutlined from "@mui/icons-material/UploadOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useBudgetDrawsApi } from "../../../../api/useBudgetDrawsApi";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import { ACTIVE_LEVEL, selectedStateSx } from "../../../../theme/activeStates";
import { useLoanExtractsIndex } from "../LoanDrilldownDocumentsTab/extracts/useLoanExtractsIndex";
import { T, money } from "./budgetDrawsTheme";
import FeasibilityPerPropertyTable from "./FeasibilityPerPropertyTable";
import {
  BUDGET_RECOMMENDATION_LABEL,
  BUDGET_RECOMMENDATION_TONE,
  buildFeasibility,
  appraisalPhotoReuseLine,
  DESK_REVIEW_LINE,
  REVIEW_BASIS_LABEL,
  reportFacts,
} from "./feasibilityModel";
import ReportPhotoStrip from "./ReportPhotoStrip";
import {
  docName,
  FeasibilityAssignMenu,
  useFeasibilityDocActions,
} from "./useFeasibilityDocActions";

/**
 * What becomes answerable once a feasibility is extracted, in the operator's
 * words. The first three are budget review's cross-checks
 * (`feasibility_cross_reference` + `completed_funded_finding`,
 * app/services/budget_review/review.py); the fourth comes from the report-photo
 * review (`reviewBasis`, app/models/report_photos.py) — a different agent, so
 * don't attribute them all to one.
 */
const PENDING_CHECKS = [
  "How much of the total budget the loan is financing",
  "Borrower budget vs vendor recommendation (5% threshold)",
  "Line items already complete or funded before closing",
  "Whether the vendor visited the site",
];

const TONE = {
  warn: { bg: T.amberTint, line: T.amberLine, ink: T.amberInk, dot: T.amber },
  info: {
    bg: T.accentTint,
    line: T.accentTint2,
    ink: T.accentInk,
    dot: T.accent,
  },
  good: { bg: T.greenTint, line: T.greenLine, ink: T.green, dot: T.green },
  neutral: { bg: T.subtle, line: T.line, ink: T.muted, dot: T.faint },
  critical: { bg: "#FDECEA", line: "#F3C9C4", ink: T.red, dot: T.red },
};

/* ── header status pill ──────────────────────────────────────────────────── */
const StatusPill = ({ tone, label }) => {
  const c = TONE[tone] || TONE.neutral;
  return (
    <Flx
      ac
      g={0.625}
      sx={{
        px: 1.125,
        py: "3px",
        borderRadius: "999px",
        background: c.bg,
        border: `1px solid ${c.line}`,
      }}
    >
      <Box
        sx={{ width: 6, height: 6, borderRadius: "50%", background: c.dot }}
      />
      <Txt sx={{ fontSize: 12, fontWeight: 600, color: c.ink }}>{label}</Txt>
    </Flx>
  );
};

/**
 * The state strip: where the feasibility stands in one line, and the actions
 * that change it.
 *
 * It ALWAYS renders — including when nothing is wrong, and including while the
 * section is collapsed — because Manage lives inside it. A quiet "on file"
 * variant keeps the actions in one fixed place rather than moving the button
 * between the header and the box depending on the loan's state.
 */
const Callout = ({ tone, lead, rest, children }) => {
  const c = TONE[tone] || TONE.neutral;
  const Icon =
    tone === "warn"
      ? ErrorOutlineRounded
      : tone === "good"
        ? CheckCircleOutlineRounded
        : InfoOutlined;
  return (
    <Flx
      ac
      jb
      g={1.5}
      sx={{
        mt: 1.5,
        pl: 1.5,
        pr: 1,
        py: 1,
        borderRadius: T.radiusSm,
        background: c.bg,
        border: `1px solid ${c.line}`,
        flexWrap: "wrap",
      }}
    >
      <Flx g={1} sx={{ minWidth: 0, py: 0.25 }}>
        <Icon sx={{ fontSize: 17, color: c.ink, flexShrink: 0, mt: "1px" }} />
        <Txt sx={{ fontSize: 13, color: c.ink, lineHeight: 1.5 }}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            {lead}
          </Box>{" "}
          {rest}
        </Txt>
      </Flx>
      {children}
    </Flx>
  );
};

/* ── the five-step rail ──────────────────────────────────────────────────── */
/**
 * Five equal columns, every step CENTRED on its own circle — label and sub-line
 * under the dot rather than flush left of it, so the row reads as one timeline
 * instead of five left-aligned blocks. Equal columns are what let the rail span
 * the full width: the connector runs centre-to-centre, so the segments are all
 * the same length and the row ends with the last step centred in its share
 * rather than the rail stopping short with dead space after it.
 */
const StepRail = ({ steps, currentIdx }) => (
  <Box
    sx={{
      mt: 2.5,
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        md: `repeat(${steps.length}, minmax(0, 1fr))`,
      },
      gap: { xs: 1.5, md: 0 },
    }}
  >
    {steps.map((step, i) => {
      const current = i === currentIdx;
      const ink = step.done || current ? T.ink : T.faint;
      const subInk = current ? T.amberInk : step.done ? T.muted : T.faint;
      const last = i === steps.length - 1;
      return (
        <Box
          key={step.key}
          sx={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            px: 1,
          }}
        >
          {/* Circle + the connector out to the next step. The connector is
              teal when the step it LEAVES is complete, and runs from this
              circle's edge to the next circle's edge — hence the ±50% (one
              half-column each side, since the columns are equal). */}
          <Box
            sx={{ position: "relative", width: "100%", height: 18, mb: 1.25 }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                mx: "auto",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                zIndex: 1,
                background: step.done ? T.accent : "#fff",
                border: step.done
                  ? `2px solid ${T.accent}`
                  : `2px solid ${current ? T.amber : T.line}`,
                boxSizing: "border-box",
              }}
            >
              {step.done && (
                <CheckRounded sx={{ fontSize: 12, color: "#fff" }} />
              )}
            </Box>
            {!last && (
              <Box
                sx={{
                  display: { xs: "none", md: "block" },
                  position: "absolute",
                  left: "calc(50% + 13px)",
                  right: "calc(-50% + 13px)",
                  top: 8,
                  height: 2,
                  borderRadius: 1,
                  background: step.done ? T.accent : T.line,
                }}
              />
            )}
          </Box>
          <Txt
            component="div"
            sx={{
              fontSize: 13.5,
              fontWeight: 600,
              color: ink,
              lineHeight: 1.4,
              maxWidth: "100%",
            }}
          >
            {step.label}
          </Txt>
          <Txt
            component="div"
            sx={{
              fontSize: 12.5,
              color: subInk,
              lineHeight: 1.4,
              mt: "2px",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {step.sub}
          </Txt>
        </Box>
      );
    })}
  </Box>
);

/* ── findings (derived — see feasibilityModel) ───────────────────────────── */
const FindingRow = ({ finding }) => {
  const c = finding.severity === "critical" ? TONE.critical : TONE.warn;
  return (
    <Box
      sx={{
        border: `1px solid ${T.line}`,
        borderRadius: T.radiusSm,
        px: 1.75,
        py: 1.5,
      }}
    >
      <Flx ac g={1} sx={{ flexWrap: "wrap" }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: c.dot,
            flexShrink: 0,
          }}
        />
        <Txt sx={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>
          {finding.title}
        </Txt>
        <Box
          sx={{
            px: 0.875,
            py: "2px",
            borderRadius: "5px",
            background: c.bg,
            color: c.ink,
            fontSize: 11.5,
            fontWeight: 600,
          }}
        >
          {finding.chip}
        </Box>
      </Flx>
      <Txt
        component="div"
        sx={{ fontSize: 12.5, color: T.muted, mt: 0.625, ml: 2 }}
      >
        {finding.description}
      </Txt>
    </Box>
  );
};

/* ── left column ─────────────────────────────────────────────────────────── */
/* ── left column ─────────────────────────────────────────────────────────── */

/**
 * The vendor's recommendation on the budget.
 *
 * Wording and tone are PRESCRIBED (`budgets-draws-report-photos.md`,
 * "Behaviour"): the sentence reads "Vendor recommends a higher budget: +$973,365
 * (recommended $2,207,015 vs proposed $1,233,650)", amber for `increase`, blue
 * for `decrease`, neutral for `adequate` / `not_stated`, with
 * `budgetRecommendationNote` beneath it. The amount is signed (recommended −
 * proposed) and the backend derives all three fields from the report's own
 * totals (`derive_budget_recommendation`, report_photos.py:154) — so this is
 * the ONE place that comparison is stated. It is not also a finding.
 */
const RecommendationBanner = ({ review }) => {
  const rec = review?.budgetRecommendation;
  if (!rec) return null;
  const c = TONE[BUDGET_RECOMMENDATION_TONE[rec] || "neutral"];
  const delta = review.budgetRecommendationAmount;
  const proposed = review.totalProposedBudget;
  const recommended = review.totalRecommendedBudget;
  const headline =
    rec === "increase" || rec === "decrease"
      ? `${BUDGET_RECOMMENDATION_LABEL[rec]}: ${delta > 0 ? "+" : "−"}${money(Math.abs(delta))}${
          recommended && proposed
            ? ` (recommended ${money(recommended)} vs proposed ${money(proposed)})`
            : ""
        }`
      : BUDGET_RECOMMENDATION_LABEL[rec];
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.125,
        borderRadius: T.radiusSm,
        background: c.bg,
        border: `1px solid ${c.line}`,
      }}
    >
      <Txt
        sx={{ fontSize: 13, fontWeight: 600, color: c.ink, lineHeight: 1.5 }}
      >
        {headline}
      </Txt>
      {review.budgetRecommendationNote && (
        <Txt
          component="div"
          sx={{ fontSize: 12.5, color: c.ink, opacity: 0.85, mt: 0.5 }}
        >
          {review.budgetRecommendationNote}
        </Txt>
      )}
    </Box>
  );
};

/**
 * The two completion figures, which the doc says "must never be confused":
 * the inspector's printed figure and the AI's reading of the photos. The exact
 * labels and the tooltip are mandated, and so is the rule that the AI figure is
 * NEVER shown on its own — so the pair renders only when the inspector's figure
 * exists, and neither is ever used in a calculation here.
 */
const CompletionFigures = ({ review }) => {
  const stated = review?.statedPercentComplete;
  if (stated == null) return null;
  const ai = review?.summary?.visiblePercentCompleteEstimate;
  return (
    <Flx ac g={1.5} sx={{ flexWrap: "wrap" }}>
      <Txt sx={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
        {`Inspector: ${Math.round(stated)}% complete`}
      </Txt>
      {ai != null && (
        <Tooltip title="An AI reading of the photos in this report only. A cross-check, not a measurement, and never a basis for funding.">
          <Flx ac g={0.5} sx={{ cursor: "help" }}>
            <Txt sx={{ fontSize: 13, color: T.muted }}>
              {`AI photo estimate: ${Math.round(ai)}%`}
            </Txt>
            <InfoOutlined sx={{ fontSize: 13, color: T.faint }} />
          </Flx>
        </Tooltip>
      )}
    </Flx>
  );
};

const ChecksBlock = ({ review, api, dealId, onOpenLine, onPreviewReport }) => {
  if (!review) {
    return (
      <Box
        sx={{
          border: `1px dashed ${T.line}`,
          borderRadius: T.radiusSm,
          px: 2,
          py: 1.75,
          background: T.subtle,
        }}
      >
        <Txt sx={{ fontSize: 13.5, color: T.ink2 }}>
          These checks run once the report is on file and extracted:
        </Txt>
        <Box component="ul" sx={{ m: 0, mt: 1.25, pl: 0, listStyle: "none" }}>
          {PENDING_CHECKS.map((c) => (
            <Flx key={c} g={1} sx={{ mb: 0.875 }}>
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: T.faint,
                  mt: "7px",
                  flexShrink: 0,
                }}
              />
              <Txt sx={{ fontSize: 13, color: T.faint, lineHeight: 1.5 }}>
                {c}
              </Txt>
            </Flx>
          ))}
        </Box>
      </Box>
    );
  }

  // Extracted: what the report actually said, rather than what will run.
  return (
    <Box
      sx={{
        border: `1px solid ${T.line}`,
        borderRadius: T.radiusSm,
        px: 2,
        py: 1.75,
      }}
    >
      <Txt sx={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>
        What the report says
      </Txt>

      <Flx column g={1.25} sx={{ mt: 1.25 }}>
        <RecommendationBanner review={review} />
        <CompletionFigures review={review} />

        {/* The report's own site photos. A desk review / image-less report
            renders the neutral one-liner instead of an empty strip. */}
        <ReportPhotoStrip
          api={api}
          dealId={dealId}
          review={review}
          onOpenLine={onOpenLine}
          onPreviewReport={onPreviewReport}
        />

        {/* A desk review is acceptable — the doc is explicit that it must be
            VISIBLE and neutral, "information, not an error", with this exact
            sentence. */}
        {review.reviewBasis === "desk_review_no_site_visit" && (
          <Txt sx={{ fontSize: 12.5, color: T.muted, lineHeight: 1.5 }}>
            {DESK_REVIEW_LINE}
          </Txt>
        )}
        {/* The strip stays (the photos are real, they're the appraisal's), but
            the basis is a desk review — never "Site visit". */}
        {review.reviewBasis === "desk_review_appraisal_photos" && (
          <Txt sx={{ fontSize: 12.5, color: T.muted, lineHeight: 1.5 }}>
            {REVIEW_BASIS_LABEL.desk_review_appraisal_photos}.
          </Txt>
        )}
        {appraisalPhotoReuseLine(review) && (
          <Txt sx={{ fontSize: 12.5, color: T.muted, lineHeight: 1.5 }}>
            {appraisalPhotoReuseLine(review)}
          </Txt>
        )}

        {/* siteVisitAgreement:false means the report's TEXT and its PHOTOS
            disagree about a site visit. The doc says to show the note — it is a
            reconciliation, not a finding, so it is not promoted to one. */}
        {review.siteVisitAgreement === false && (
          <Flx g={1}>
            <InfoOutlined
              sx={{ fontSize: 15, color: T.muted, flexShrink: 0, mt: "2px" }}
            />
            <Txt sx={{ fontSize: 12.5, color: T.muted, lineHeight: 1.5 }}>
              {review.note ||
                "The report's text and its photos disagree about a site visit; the photo record governs."}
            </Txt>
          </Flx>
        )}

        {review.summary?.workInPlace && (
          <Txt
            component="div"
            sx={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.55 }}
          >
            {review.summary.workInPlace}
          </Txt>
        )}

        {review.summary?.concerns?.length > 0 && (
          <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
            {review.summary.concerns.map((c, i) => (
              <Box
                component="li"
                key={i}
                sx={{
                  fontSize: 12.5,
                  color: T.ink2,
                  lineHeight: 1.55,
                  mb: 0.25,
                }}
              >
                {c}
              </Box>
            ))}
          </Box>
        )}
      </Flx>
    </Box>
  );
};

/* ── right column ────────────────────────────────────────────────────────── */
const ReportPanel = ({
  review,
  groups,
  activeKey,
  onSelectGroup,
  docs,
  busy,
  onAssign,
  onUpload,
  onPreview,
}) => (
  <Box>
    <Txt sx={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Report</Txt>

    {/* A multi-property loan can carry one feasibility per property, and a
        report whose subjectPropertyId didn't resolve sits under "Unassigned"
        (report-photos doc). Switching here changes DATA, not the page — level 2
        per the active-states convention. */}
    {groups.length > 1 && (
      <Flx ac g={0.75} sx={{ mt: 1.25, flexWrap: "wrap" }}>
        {groups.map((g) => {
          const key = g.propertyId || "unassigned";
          const active = key === activeKey;
          return (
            <Box
              key={key}
              component="button"
              type="button"
              onClick={() => onSelectGroup(key)}
              sx={{
                maxWidth: 200,
                px: 1.125,
                py: "4px",
                borderRadius: "999px",
                border: `1px solid ${T.line}`,
                background: "#fff",
                color: T.muted,
                fontFamily: "inherit",
                fontSize: 11.5,
                fontWeight: 500,
                cursor: "pointer",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                ...(active
                  ? selectedStateSx({
                      level: ACTIVE_LEVEL.DATA,
                      variant: "circle",
                    })
                  : {}),
              }}
            >
              {g.label}
            </Box>
          );
        })}
      </Flx>
    )}

    {docs?.length ? (
      <Flx column g={0.75} sx={{ mt: 1.25 }}>
        {docs.slice(0, 3).map((doc) => (
          <Tooltip key={doc._id} title={`Preview ${docName(doc)}`}>
            <Box
              component="button"
              type="button"
              onClick={() => onPreview(doc)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                width: "100%",
                px: 1.25,
                py: 0.875,
                borderRadius: T.radiusSm,
                border: `1px solid ${T.greenLine}`,
                background: T.greenTint,
                color: T.green,
                fontFamily: "inherit",
                fontSize: 12.5,
                fontWeight: 600,
                textAlign: "left",
                cursor: "pointer",
                "&:hover": { background: T.greenTint2 },
              }}
            >
              <DescriptionOutlined sx={{ fontSize: 15, flexShrink: 0 }} />
              <Box
                component="span"
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {docName(doc)}
              </Box>
            </Box>
          </Tooltip>
        ))}
        {docs.length > 3 && (
          <Txt sx={{ fontSize: 12, color: T.muted }}>
            +{docs.length - 3} more on the loan
          </Txt>
        )}
      </Flx>
    ) : (
      <Flx
        column
        ac
        g={1.25}
        sx={{
          mt: 1.25,
          px: 2,
          py: 2.25,
          borderRadius: T.radiusSm,
          border: `1px dashed ${T.line}`,
          background: T.subtle,
        }}
      >
        <Txt sx={{ fontSize: 13, color: T.muted }}>No report on file yet.</Txt>
        <Flx ac g={1}>
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            startIcon={<LinkOutlined />}
            onClick={onAssign}
            sx={{ textTransform: "none", fontSize: 12.5, borderRadius: "7px" }}
          >
            Assign existing
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            startIcon={<UploadOutlined />}
            onClick={onUpload}
            sx={{ textTransform: "none", fontSize: 12.5, borderRadius: "7px" }}
          >
            Upload
          </Button>
        </Flx>
      </Flx>
    )}

    <Box sx={{ mt: 2 }}>
      {reportFacts(review).map((f) => (
        <Flx
          key={f.label}
          ac
          jb
          g={2}
          sx={{ py: 0.75, borderTop: `1px solid ${T.line2}` }}
        >
          <Txt sx={{ fontSize: 12.5, color: T.muted }}>{f.label}</Txt>
          <Txt
            sx={{
              fontSize: 12.5,
              fontWeight: f.value ? 600 : 400,
              color: f.value ? T.ink : T.faint,
              textAlign: "right",
            }}
          >
            {f.value || "—"}
          </Txt>
        </Flx>
      ))}
    </Box>
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════ */

const FeasibilitySection = ({
  dealId,
  reviews,
  onOpenLine,
  onPreviewReport,
}) => {
  const dispatch = useDispatch();
  const api = useBudgetDrawsApi();
  // Collapsed by default: the header alone (title · status pill · assign /
  // upload) answers "where does the feasibility stand" for the common case, and
  // Budget & Draws is really about the workbook below. Expand for the detail.
  const [open, setOpen] = useState(false);
  const [manageAnchor, setManageAnchor] = useState(null);

  const productType = useSelector((s) => s.loanDrilldown?.loanProductType);
  const properties = useSelector((s) => s.loanDrilldown?.subjectProperties);
  const loanDocuments = useSelector((s) => s.loanDrilldown?.loanDocuments);

  const { documents: extractDocs } = useLoanExtractsIndex(dealId, {
    enabled: Boolean(dealId),
  });

  const model = useMemo(
    () =>
      buildFeasibility({
        productType,
        properties,
        loanDocuments,
        extractDocs,
        reviews,
      }),
    [productType, properties, loanDocuments, extractDocs, reviews],
  );

  const {
    busy,
    canExtract,
    runExtraction,
    openAssign,
    startUpload,
    menuProps,
    inputProps,
  } = useFeasibilityDocActions({ dealId, docs: model.docs });

  const conflictIds = useMemo(
    () => new Set(model.conflictRows.map((r) => r.id)),
    [model.conflictRows],
  );

  // Which report the right column is showing on a multi-property loan. Falls
  // back to the first group whenever the selection no longer exists.
  const [groupKey, setGroupKey] = useState(null);
  const activeGroup =
    model.reviewGroups.find(
      (g) => (g.propertyId || "unassigned") === groupKey,
    ) ||
    model.reviewGroups[0] ||
    null;
  const activeKey = activeGroup ? activeGroup.propertyId || "unassigned" : null;

  // Not expected on this product and nobody ordered one — nothing to report.
  if (model.empty) return null;

  const preview = (doc) => {
    const full = (loanDocuments || []).find((d) => d?._id === doc?._id) || doc;
    if (full?._id) dispatch(filePreviewSetData(full));
  };

  return (
    <Box
      sx={{
        bgcolor: T.card,
        border: `1px solid ${T.line}`,
        borderRadius: T.radius,
        boxShadow: T.shadow,
        overflow: "hidden",
      }}
    >
      {/* HEADER */}
      <Box sx={{ px: 2.5, pt: 2, pb: open ? 2.25 : 2 }}>
        <Flx ac jb g={2} sx={{ flexWrap: "wrap" }}>
          <Flx ac g={1.25} sx={{ minWidth: 0 }}>
            <Txt sx={{ fontSize: 16, fontWeight: 700, color: T.ink }}>
              Feasibility report
            </Txt>
            <StatusPill {...model.status} />
            {busy && (
              <CircularProgress
                size={13}
                thickness={5}
                sx={{ color: T.faint }}
              />
            )}
          </Flx>
          <Flx ac g={1}>
            <IconButton
              size="small"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Collapse feasibility" : "Expand feasibility"}
              sx={{ color: T.muted }}
            >
              <KeyboardArrowDownRounded
                sx={{
                  fontSize: 22,
                  transition: "transform .15s",
                  transform: open ? "none" : "rotate(-90deg)",
                }}
              />
            </IconButton>
          </Flx>
        </Flx>

        {/* The state strip stays visible through the collapse: it carries both
            the one thing wrong ("marked received, but no report is on file" —
            hiding that behind a chevron is how it gets missed) and Manage. */}
        {model.callout && (
          <Callout {...model.callout}>
            {/* ONE contained action. The individual verbs (assign / upload /
                extract) live in its popover rather than competing for the
                strip — and the popover is where a new one would go too. */}
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={busy}
              endIcon={
                <KeyboardArrowDownRounded sx={{ fontSize: 18, ml: -0.25 }} />
              }
              onClick={(e) => setManageAnchor(e.currentTarget)}
              sx={{
                textTransform: "none",
                fontSize: 13,
                borderRadius: "8px",
                flexShrink: 0,
              }}
            >
              Manage
            </Button>
            <Menu
              anchorEl={manageAnchor}
              open={Boolean(manageAnchor)}
              onClose={() => setManageAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{ paper: { sx: { minWidth: 232 } } }}
            >
              <MenuItem
                onClick={(e) => {
                  setManageAnchor(null);
                  openAssign(e);
                }}
              >
                <ListItemIcon>
                  <LinkOutlined sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Assign existing"
                  secondary="Re-type a document already on the loan"
                  slotProps={{
                    primary: { sx: { fontSize: 13.5 } },
                    secondary: { sx: { fontSize: 11.5 } },
                  }}
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setManageAnchor(null);
                  startUpload();
                }}
              >
                <ListItemIcon>
                  <UploadOutlined sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Upload report"
                  secondary="Lands classified as a feasibility"
                  slotProps={{
                    primary: { sx: { fontSize: 13.5 } },
                    secondary: { sx: { fontSize: 11.5 } },
                  }}
                />
              </MenuItem>
              {/* Only offered once something is on file to parse. */}
              {canExtract && !model.extracted && (
                <MenuItem
                  onClick={() => {
                    setManageAnchor(null);
                    runExtraction();
                  }}
                >
                  <ListItemIcon>
                    <AutoAwesomeOutlined sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Run extraction"
                    secondary="Parse the report on file"
                    slotProps={{
                      primary: { sx: { fontSize: 13.5 } },
                      secondary: { sx: { fontSize: 11.5 } },
                    }}
                  />
                </MenuItem>
              )}
            </Menu>
          </Callout>
        )}
        <Collapse in={open}>
          <StepRail steps={model.steps} currentIdx={model.currentIdx} />
        </Collapse>
      </Box>

      <Collapse in={open}>
        {/* BODY — checks + findings | the report */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 340px" },
            borderTop: `1px solid ${T.line}`,
          }}
        >
          <Box sx={{ px: 2.5, py: 2.25, minWidth: 0 }}>
            <ChecksBlock
              review={activeGroup?.review}
              api={api}
              dealId={dealId}
              onOpenLine={onOpenLine}
              onPreviewReport={onPreviewReport}
            />
            {model.findings.length > 0 && (
              <Box sx={{ mt: 2.25 }}>
                <Flx ac g={1} sx={{ mb: 1.25 }}>
                  <Txt sx={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>
                    Findings
                  </Txt>
                  <Txt sx={{ fontSize: 12.5, color: T.muted }}>
                    {model.findings.length}
                  </Txt>
                </Flx>
                <Flx column g={1}>
                  {model.findings.map((f) => (
                    <FindingRow key={f.key} finding={f} />
                  ))}
                </Flx>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              px: 2.5,
              py: 2.25,
              minWidth: 0,
              borderLeft: { lg: `1px solid ${T.line}` },
              borderTop: { xs: `1px solid ${T.line}`, lg: "none" },
            }}
          >
            <ReportPanel
              review={activeGroup?.review}
              groups={model.reviewGroups}
              activeKey={activeKey}
              onSelectGroup={setGroupKey}
              docs={model.docs}
              busy={busy}
              onAssign={openAssign}
              onUpload={startUpload}
              onPreview={preview}
            />
          </Box>
        </Box>

        {/* PER PROPERTY */}
        <Box sx={{ borderTop: `1px solid ${T.line}` }}>
          <FeasibilityPerPropertyTable
            dealId={dealId}
            rows={model.perProperty}
            conflictIds={conflictIds}
            required={model.required}
          />
        </Box>
      </Collapse>

      <FeasibilityAssignMenu {...menuProps} />
      <input {...inputProps} />
    </Box>
  );
};

FeasibilitySection.propTypes = {
  dealId: PropTypes.string,
  /** ReportPhotoReview[] for the deal — fetched once by BudgetDrawsTab. */
  reviews: PropTypes.array,
  onOpenLine: PropTypes.func,
  onPreviewReport: PropTypes.func,
};

export default FeasibilitySection;
